import React, { useEffect, useState } from 'react';
import { createPortal} from 'react-dom'
import Icon from './Icon'
import Image from './Image'

interface LensTechIconProps {
  heading: string
  description: string
  icon: string
}

export interface LensTechDrawerProps {
  lensTechIcon?: LensTechIconProps[]
  isOpen: boolean
  onClose: () => void
}

const LensTechDrawer: React.FC<LensTechDrawerProps> = ({ lensTechIcon, isOpen, onClose}) => {
  const [mounted, setMounted] = useState<boolean>(false);
  const [shouldAnimate, setShouldAnimate] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Small delay to ensure initial state is rendered
      const timer = setTimeout(() => setShouldAnimate(true), 10);
      const drawer = document.getElementById('lens-tech-drawer');
      if (drawer) {
        drawer?.focus();
      }
      return () => clearTimeout(timer);
    } else {
      setShouldAnimate(false);
    }
  }, [isOpen]);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Handle ESC key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!mounted || !isOpen) return null;

  return createPortal (
    <>
      <div
        className={`w-full block w-full h-full fixed inset-0 z-30 bg-black backdrop-filter backdrop-blur-md backdrop-saturate-150 cursor-pointer transition delay-500 duration-500 ease-in-out ${isOpen ? 'bg-opacity-20' : 'bg-opacity-0'}`}
        onClick={onClose}
        aria-hidden="true"
      ></div>

      {/* Drawer */}
      <div className={`block max-w-panel w-full h-full fixed top-0 bottom-0 transition duration-500 ease-in-out z-[38] overflow-scroll right-0 bg-[#E8EBED] ${shouldAnimate ? 'translate-x-0' : 'translate-x-full'}`}
           id="lens-tech-drawer" role="dialog" aria-modal="true" aria-label="Similar Variants" aria-expanded="true" tabIndex={-1}>
        <div className="px-4 mb-20">
          <div className="sticky top-0 bg-[#E8EBED] py-4 z-10">
            <button onClick={onClose} className="cursor-pointer absolute right-0 p-2" type="button"
                    aria-label="Close Similar Variants Drawer">
              <Icon name="close" className="text-black w-5 h-5" strokeWidth={2} />
            </button>
            <h2 className="body-price">Lens Tech</h2>
          </div>

          <div className="mx-4 mt-4">
            {lensTechIcon && (
              lensTechIcon.map((icon) => {
                return (
                  <div className="flex gap-4 mb-8 items-center" key={icon.heading}>
                    <div className="flex-none w-16">
                      <Image src={icon.icon} alt={icon.heading} width={80} height={80} />
                    </div>
                    <div className="flex flex-col flex-1">
                      <p className="body-md font-bold">{icon.heading}</p>
                      <p dangerouslySetInnerHTML={{ __html: icon.description || '' }} className="body-sm"></p>
                    </div>

                  </div>
                )
              })
            )}
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default LensTechDrawer;